import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
    LayoutDashboard, 
    Activity, 
    Database, 
    Settings, 
    Bell,
    ChevronLeft,
    ChevronRight,
    Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';

const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/anomalies', label: 'Anomalies', icon: Activity },
    { path: '/data-sources', label: 'Data Sources', icon: Database },
    { path: '/alerts', label: 'Alerts', icon: Bell },
    { path: '/settings', label: 'Settings', icon: Settings }
];

export const Layout = ({ children, alertCount = 0 }) => {
    const [collapsed, setCollapsed] = useState(false);
    const location = useLocation();

    return (
        <div className="min-h-screen bg-background flex">
            {/* Sidebar */}
            <aside 
                className={cn(
                    "fixed left-0 top-0 h-full bg-card/50 backdrop-blur-xl border-r border-border z-50 transition-all duration-300",
                    collapsed ? "w-16" : "w-64"
                )}
            >
                {/* Logo */}
                <div className="h-16 flex items-center justify-between px-4 border-b border-border">
                    {!collapsed && (
                        <div className="flex items-center gap-2">
                            <Eye className="h-6 w-6 text-primary" />
                            <span className="font-bold text-lg text-card-foreground">KPI Watchtower</span>
                        </div>
                    )}
                    {collapsed && <Eye className="h-6 w-6 text-primary mx-auto" />}
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setCollapsed(!collapsed)}
                        className={cn("h-8 w-8", collapsed && "mx-auto mt-2")}
                        data-testid="sidebar-toggle"
                    >
                        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
                    </Button>
                </div>

                {/* Navigation */}
                <nav className="p-2 space-y-1">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.path}
                            to={item.path}
                            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                            className={({ isActive }) => cn(
                                "flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors duration-200",
                                isActive 
                                    ? "bg-primary/10 text-primary border border-primary/20" 
                                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                            )}
                        >
                            <item.icon className="h-5 w-5 flex-shrink-0" />
                            {!collapsed && (
                                <span className="text-sm font-medium">{item.label}</span>
                            )}
                            {!collapsed && item.path === '/alerts' && alertCount > 0 && (
                                <Badge variant="destructive" className="ml-auto">
                                    {alertCount}
                                </Badge>
                            )}
                        </NavLink>
                    ))}
                </nav>
            </aside>

            {/* Main Content */}
            <main className={cn(
                "flex-1 transition-all duration-300",
                collapsed ? "ml-16" : "ml-64"
            )}>
                <div className="min-h-screen p-6 lg:p-8">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default Layout;
